import { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import logo from "../../../assets/images/logo/Trellis_Logomark_color.png";
import dynamicPricingTemplateUrl from "/dynamic_pricing_template.csv?url";
import pricingRelationshipsUrl from "/pricing_relationships.csv?url";
import purchaseOrderTemplateUrl from "/purchase_order_template.csv?url";
import Papa from "papaparse";
import type { ParseResult } from "papaparse";
import { useMutation } from "@tanstack/react-query";
import api from "../../../utils/api";
import { useAppSelector } from "../../../redux/hooks";
import { useMarketplaceOptions } from "../../../utils/marketplaceConstants";
import type { FunctionComponent } from "react";
import { Modal } from "../../core/basic/Modal";
import { Button } from "../../core/basic/Button";
import { Select, SelectItem } from "../../fields/base/Select";
import { css } from "../../../../styled-system/css";
import { Wrap } from "../../../../styled-system/jsx";
import FileDropZone from "../../core/blocks/FileDropZone";

const previewTableContainerStyles = css({
  maxHeight: "440px",
  marginTop: "8px",
  overflow: "auto",
});

const progressStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "24px",
});

const tableStyles = css({
  width: "100%",
  borderCollapse: "collapse",
  "& th, & td": {
    padding: "8px 16px",
    textAlign: "left",
    borderBottom: "1px solid #E0E0E0",
  },
  "& thead th": {
    position: "sticky",
    top: 0,
    backgroundColor: "#FAFAFA",
    fontWeight: "bold",
  },
});

const stepperStyles = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "0",
  paddingX: "large",
  paddingY: "small",
});

const stepCircleStyles = css({
  width: "24px",
  height: "24px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: "bold",
  flexShrink: 0,
});

const stepConnectorStyles = css.raw({
  height: "1px",
  width: "60px",
  backgroundColor: "#E0E0E0",
  marginX: "8px",
});

const stepConnectorActiveStyles = css.raw({
  backgroundColor: "#7C3AED",
});

export type ImportType = "master" | "channel" | "pricing_bulk_upload" | "pricing_relationships" | "product_po_history";

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  importType: ImportType;
  selectedOrg: string | number;
}

interface ApiErrorResponse {
  response: {
    data: unknown;
  };
}

interface UploadError {
  error?: {
    msg?: string;
  };
  mapped?: {
    sku?: string;
  };
}

interface ParseError {
  error: UploadError;
}

interface MatchedBy {
  reason: string;
  sku: string;
  asin: string;
}

interface UploadSuccess extends Pick<UploadError, "mapped"> {
  matched_by?: MatchedBy;
}

interface UploadResults {
  errors: ParseError[];
  warnings: UploadSuccess[];
  count: number;
}

interface RowData {
  [key: string]: string;
}

interface UploadProductData {
  row: RowData;
  columns: Record<string, string>;
  import_type: ImportType;
  selected_org: { id: string | number };
  make_missing_categories: boolean;
  update_existing_products: boolean;
  marketplace: string;
}

interface UploadProductAction {
  data: UploadProductData;
  onSuccess: (result: UploadSuccess) => void;
  onError: (error: UploadError) => void;
}

function ResultTable({
  secondColumnLabel,
  rows,
  emptyMessage,
}: {
  secondColumnLabel: string;
  rows: { key: string; sku?: string; message?: string }[];
  emptyMessage?: string;
}) {
  const { t } = useTranslation(["merchandising"]);

  return (
    <div className={previewTableContainerStyles}>
      <table className={tableStyles}>
        <thead>
          <tr>
            <th>{t(($) => $.merchandising["SKU"])}</th>
            <th>{secondColumnLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && emptyMessage ?
            <tr>
              <td colSpan={2}>{emptyMessage}</td>
            </tr>
          : rows.map((row) => (
              <tr key={row.key}>
                <td>{row.sku}</td>
                <td>{row.message}</td>
              </tr>
            ))
          }
        </tbody>
      </table>
    </div>
  );
}

function StepIndicator({ step, label, activeStep }: { step: number; label: string; activeStep: number }) {
  const isCompleted = activeStep > step;
  const isActive = activeStep === step;

  return (
    <div className={css({ display: "flex", alignItems: "center", gap: "8px" })}>
      <div
        className={stepCircleStyles}
        style={{
          backgroundColor: isCompleted || isActive ? "#7C3AED" : "#E0E0E0",
          color: isCompleted || isActive ? "white" : "#999",
        }}
      >
        {isCompleted ? "\u2713" : step + 1}
      </div>
      <span
        className={css({ fontSize: "small" })}
        style={{
          color: isActive ? "#333" : "#999",
          fontWeight: isActive ? 600 : 400,
        }}
      >
        {label}
      </span>
    </div>
  );
}

export const ImportDialog: FunctionComponent<ImportDialogProps> = (props) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const marketplaceOptions = useMarketplaceOptions();
  const yesNoOptions = useMemo(
    () =>
      [
        { value: "yes", label: t(($) => $.merchandising["Yes"]) },
        { value: "no", label: t(($) => $.merchandising["No"]) },
      ] as const,
    [t],
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [parsedResults, setParsedResults] = useState<ParseResult<RowData> | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResults>({
    errors: [],
    warnings: [],
    count: 0,
  });
  const [makeMissingCategories, setMakeMissingCategories] = useState("yes");
  const [updateExistingProducts, setUpdateExistingProducts] = useState("yes");
  const [aborted, setAborted] = useState(false);

  const channel = useAppSelector((state) => state.channel);
  const marketplace = useAppSelector((state) => state.marketPlace.marketPlace);

  const uploadProduct = useMutation({
    mutationFn: async (action: UploadProductAction) => {
      return await api.post(`/api/v2/import/`, action.data);
    },

    onSuccess: (response: { data: unknown }, action: UploadProductAction) => {
      action.onSuccess(response.data as UploadSuccess);
    },

    onError: (error: ApiErrorResponse, action: UploadProductAction) => {
      action.onError(error.response.data as UploadError);
    },
  });

  const _onClose = useCallback(() => {
    // Disable closing the dialog if we're in the middle of the import step.
    if (activeStep !== 1) {
      props.onClose();
    }
  }, [activeStep, props]);

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    setIsLoading(true);

    Papa.parse<RowData>(file, {
      header: true,
      preview: 25,
      complete: (results) => {
        setIsLoading(false);
        setParsedResults(results);
      },
    });
  }, []);

  // Called when the user hits the final "Import" button.
  const onImport = useCallback(() => {
    if (!selectedFile) {
      // No file selected, exit early
      return;
    }

    setActiveStep(1);

    Papa.parse<RowData>(selectedFile, {
      header: true,
      step: (row, parser) => {
        // For now, we temporarily pause parsing until the upload finishes,
        // since we don't want to flood the server.
        parser.pause();

        uploadProduct.mutate({
          data: {
            row: row.data,
            columns: columnMapping,
            import_type: props.importType,
            selected_org: { id: props.selectedOrg },
            make_missing_categories: makeMissingCategories === "yes",
            update_existing_products: updateExistingProducts === "yes",
            marketplace: marketplace,
          },
          onSuccess: (result: UploadSuccess) => {
            setUploadResults((old) => ({
              ...old,
              count: old.count + 1,
              warnings: result.matched_by ? [...old.warnings, result] : old.warnings,
            }));
            parser.resume();
          },
          onError: (e: UploadError) => {
            setUploadResults((old) => {
              if (old.errors.length > 500) {
                // Too many errors occurred, lets give up.
                parser.abort();
                setAborted(true);
              } else {
                parser.resume();
              }

              return { ...old, errors: [...old.errors, { error: e }] };
            });
          },
        });

        setUploadProgress((old) => {
          if (!old) return 1;
          return old + 1;
        });
      },
      complete: () => {
        setUploadProgress(null);
        setActiveStep(2);
      },
    });
  }, [
    selectedFile,
    columnMapping,
    makeMissingCategories,
    marketplace,
    props.importType,
    props.selectedOrg,
    updateExistingProducts,
    uploadProduct,
  ]);

  // A list of the available column choices for the current import type.
  const availableColumns = useMemo<Array<{ value: string; label: string }>>(() => {
    if (props.importType === "master") {
      return [
        { value: "null", label: t(($) => $.merchandising["N/A"]) },
        { value: "sku", label: t(($) => $.merchandising["SKU"]) },
        { value: "title", label: t(($) => $.merchandising["Title"]) },
        { value: "description", label: t(($) => $.merchandising["Description"]) },
        { value: "landed_price", label: t(($) => $.merchandising["Landed Price"]) },
        { value: "image_url", label: t(($) => $.merchandising["Image URL"]) },
        { value: "inventory", label: t(($) => $.merchandising["Inventory"]) },
      ];
    } else if (props.importType === "channel") {
      if (channel.currentChannel === "walmart") {
        return [
          { value: "null", label: t(($) => $.merchandising["N/A"]) },
          { value: "channel_id", label: t(($) => $.merchandising["Item ID"]) },
          { value: "sku", label: t(($) => $.merchandising["SKU"]) },
          { value: "title", label: t(($) => $.merchandising["Product Title"]) },
          { value: "category", label: t(($) => $.merchandising["Product Category"]) },
          { value: "item_page_url", label: t(($) => $.merchandising["Item Page URL"]) },
          { value: "item_image_url", label: t(($) => $.merchandising["Item Image URL"]) },
          { value: "product_price", label: t(($) => $.merchandising["Product Price"]) },
          { value: "primary_variant", label: t(($) => $.merchandising["Primary Variant"]) },
          { value: "landed_cost", label: t(($) => $.merchandising["Landed Cost"]) },
          { value: "shipping_cost", label: t(($) => $.merchandising["Shipping Cost"]) },
          { value: "channel_fees", label: t(($) => $.merchandising["Channel Fees"]) },
        ];
      }

      return [
        { value: "null", label: t(($) => $.merchandising["N/A"]) },
        { value: "sku", label: t(($) => $.merchandising["SKU"]) },
        { value: "upc", label: t(($) => $.merchandising["UPC"]) },
        { value: "category", label: t(($) => $.merchandising["Category"]) },
        { value: "channel_id", label: t(($) => $.merchandising["Channel ID (ASIN)"]) },
        { value: "title", label: t(($) => $.merchandising["Title"]) },
        { value: "channel_net_price", label: t(($) => $.merchandising["Channel Net Price"]) },
        { value: "channel_gross_price", label: t(($) => $.merchandising["Channel Gross Price"]) },
      ];
    } else if (props.importType === "pricing_bulk_upload") {
      return [
        { value: "null", label: t(($) => $.merchandising["N/A"]) },
        { value: "sku", label: t(($) => $.merchandising["SKU"]) },
        { value: "channel_id", label: t(($) => $.merchandising["Channel ID (ASIN)"]) },
        { value: "landed_cost", label: t(($) => $.merchandising["Landed Cost"]) },
        { value: "shipping_cost", label: t(($) => $.merchandising["Shipping Cost"]) },
        { value: "fulfillment_channel", label: t(($) => $.merchandising["Fulfillment Channel"]) },
        { value: "min_price", label: t(($) => $.merchandising["Min Price"]) },
        { value: "max_price", label: t(($) => $.merchandising["Max Price"]) },
        { value: "fixed_price", label: t(($) => $.merchandising["Fixed Price"]) },
        { value: "channel_fee_percent", label: t(($) => $.merchandising["Channel Fee Percent"]) },
        { value: "days_on_hand_target", label: t(($) => $.merchandising["Days on Hand Target"]) },
        { value: "sales_velocity_target", label: t(($) => $.merchandising["Sales Velocity Target"]) },
        { value: "sell_price", label: t(($) => $.merchandising["New Initial Price"]) },
      ];
    } else if (props.importType === "pricing_relationships") {
      return [
        { value: "null", label: t(($) => $.merchandising["N/A"]) },
        { value: "target_sku", label: t(($) => $.merchandising["Target SKU"]) },
        { value: "reference_sku", label: t(($) => $.merchandising["Reference SKU"]) },
        { value: "constraint", label: t(($) => $.merchandising["Constraint"]) },
        { value: "constraint_value", label: t(($) => $.merchandising["% / $"]) },
        { value: "amount", label: t(($) => $.merchandising["Amount"]) },
      ];
    }
    // product_po_history
    return [
      { value: "null", label: t(($) => $.merchandising["N/A"]) },
      { value: "sku", label: t(($) => $.merchandising["SKU"]) },
      { value: "channel_id", label: t(($) => $.merchandising["Channel ID (ASIN)"]) },
      { value: "date", label: t(($) => $.merchandising["Incomming Date"]) },
      { value: "quantity", label: t(($) => $.merchandising["Incomming Quantity"]) },
    ];
  }, [props.importType, channel.currentChannel, t]);

  const importValidation = useMemo(() => {
    const mappedValues = Object.values(columnMapping);
    const has = (col: string) => mappedValues.includes(col);

    if (!selectedFile) return { isDisabled: true, errorMessage: null };

    switch (props.importType) {
      case "master":
        if (!has("sku"))
          return {
            isDisabled: true,
            errorMessage: t(($) => $.merchandising["Please map the SKU column to enable importing"]),
          };
        break;
      case "channel":
        if (channel.currentChannel === "walmart") {
          if (!has("sku") || !has("channel_id") || !has("title"))
            return {
              isDisabled: true,
              errorMessage: t(
                ($) => $.merchandising["Please map product SKUs, item IDs, and titles to enable importing"],
              ),
            };
        } else if (!has("sku") || !has("channel_id")) {
          return {
            isDisabled: true,
            errorMessage: t(($) => $.merchandising["Please map product SKUs and channel IDs to enable importing"]),
          };
        }
        break;
      case "pricing_bulk_upload":
        if (!has("sku") && !has("channel_id"))
          return {
            isDisabled: true,
            errorMessage: t(($) => $.merchandising["Please map either SKU or channel ID to enable importing"]),
          };
        break;
      case "pricing_relationships":
        if (
          !has("target_sku") ||
          !has("reference_sku") ||
          !has("constraint") ||
          !has("constraint_value") ||
          !has("amount")
        )
          return {
            isDisabled: true,
            errorMessage: t(
              ($) =>
                $.merchandising[
                  "Please map all required columns (target SKU, reference SKU, constraint, %, amount) to enable importing"
                ],
            ),
          };
        break;
      case "product_po_history":
        if (!has("sku") || !has("channel_id") || !has("date") || !has("quantity"))
          return {
            isDisabled: true,
            errorMessage: t(
              ($) => $.merchandising["Please map SKU, channel ID, date, and quantity columns to enable importing"],
            ),
          };
        break;
    }

    return { isDisabled: false, errorMessage: null };
  }, [props.importType, columnMapping, selectedFile, channel.currentChannel, t]);

  const templateUrl =
    props.importType === "pricing_bulk_upload" ? dynamicPricingTemplateUrl
    : props.importType === "pricing_relationships" ? pricingRelationshipsUrl
    : props.importType === "product_po_history" ? purchaseOrderTemplateUrl
    : null;

  return (
    <Modal
      isOpen={props.isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) _onClose();
      }}
      isKeyboardDismissDisabled={activeStep === 1}
      title={t(($) => $.merchandising["Import Wizard"])}
      css={{ width: "75vw" }}
    >
      <div className={stepperStyles}>
        <StepIndicator step={0} label={t(($) => $.merchandising["Select File"])} activeStep={activeStep} />
        <div className={css(stepConnectorStyles, activeStep > 0 && stepConnectorActiveStyles)} />
        <StepIndicator step={1} label={t(($) => $.merchandising["Import"])} activeStep={activeStep} />
        <div className={css(stepConnectorStyles, activeStep > 1 && stepConnectorActiveStyles)} />
        <StepIndicator step={2} label={t(($) => $.merchandising["Results"])} activeStep={activeStep} />
      </div>

      {templateUrl && (
        <div className={css({ paddingY: "12px", textAlign: "center" })}>
          <small>
            <a href={templateUrl}>{t(($) => $.merchandising["Download Template"])}</a>
          </small>
        </div>
      )}

      {activeStep === 0 ?
        <>
          <div className={css({ paddingX: "large", overflow: "auto" })}>
            <FileDropZone
              onFileSelect={handleFileSelect}
              label={t(($) => $.merchandising["Drop the file to be imported here, or click the button below."])}
              isPending={isLoading}
              acceptedFileTypes={[".csv", "text/csv"]}
            />
            <Wrap gap="small" paddingY="small">
              {(props.importType === "master" || props.importType === "channel") && (
                <>
                  <Select
                    label={t(($) => $.merchandising["Create Missing Categories"])}
                    description={t(
                      ($) =>
                        $.merchandising[
                          "Should we create any missing categories? If not selected, products in unknown categories will be skipped."
                        ],
                    )}
                    value={makeMissingCategories}
                    onChange={(key) => setMakeMissingCategories(key as string)}
                    items={yesNoOptions}
                    css={{ flex: "1", minWidth: "200px", maxWidth: "300px" }}
                  >
                    {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
                  </Select>
                  <Select
                    label={t(($) => $.merchandising["Update Existing Products"])}
                    description={t(
                      ($) =>
                        $.merchandising[
                          "Should we update products with matching SKUs? If no, products with matching SKUs will be skipped."
                        ],
                    )}
                    value={updateExistingProducts}
                    onChange={(key) => setUpdateExistingProducts(key as string)}
                    items={yesNoOptions}
                    css={{ flex: "1", minWidth: "200px", maxWidth: "300px" }}
                  >
                    {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
                  </Select>
                </>
              )}
              <Select
                label={t(($) => $.merchandising["Marketplace"])}
                description={t(($) => $.merchandising["What marketplace do these products belong to?"])}
                value={marketplace}
                isDisabled
                items={marketplaceOptions}
                css={{ flex: "1", minWidth: "200px", maxWidth: "300px" }}
              >
                {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
              </Select>
            </Wrap>
            {parsedResults ?
              <>
                <span>{t(($) => $.merchandising["Preview"])}</span>
                <div className={previewTableContainerStyles}>
                  <table className={tableStyles}>
                    <thead>
                      <tr>
                        {parsedResults.meta.fields?.map((column) => (
                          <th key={`header-${column}`}>
                            <div>{column}</div>
                            <Select
                              aria-label={t(($) => $.merchandising["Column mapping for {column}"], { column })}
                              value={columnMapping[column] ?? "null"}
                              onChange={(key) => {
                                setColumnMapping((old) => ({
                                  ...old,
                                  [column]: key as string,
                                }));
                              }}
                              items={availableColumns}
                              css={{ width: "150px" }}
                            >
                              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
                            </Select>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedResults.data.filter(Boolean).map((row, idx) => (
                        <tr key={`row-${idx}`}>
                          {parsedResults.meta.fields?.map((column) => (
                            <td key={`row-${idx}-${column}`}>{row[column]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            : null}
          </div>
          <div className={css({ display: "flex", justifyContent: "flex-end", gap: "small", paddingX: "large" })}>
            {importValidation.errorMessage && (
              <span className={css({ color: "red", fontSize: "small", alignSelf: "center" })}>
                {importValidation.errorMessage}
              </span>
            )}

            <Button variant="secondary" onPress={props.onClose}>
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button isDisabled={importValidation.isDisabled} onPress={onImport} variant="primary">
              {t(($) => $.merchandising["Import"])}
            </Button>
          </div>
        </>
      : activeStep === 1 ?
        <div className={css({ paddingX: "large" })}>
          <div className={progressStyles}>
            <img src={logo} alt={t(($) => $.merchandising["Loading..."])} className="rotate" width="64" />
            <div>{t(($) => $.merchandising["Uploading row {row}..."], { row: uploadProgress ?? 0 })}</div>
            <div>{t(($) => $.merchandising["Please do not close this page until complete."])}</div>
          </div>
        </div>
      : activeStep === 2 ?
        <>
          <div className={css({ paddingX: "large", overflow: "auto" })}>
            <p>
              <Trans
                t={t}
                i18nKey={($) => $.merchandising["Successfully imported <strong>{count}</strong> products."]}
                values={{ count: uploadResults.count }}
                components={{ strong: <strong /> }}
              />
            </p>

            {aborted ?
              <p>{t(($) => $.merchandising["Import was aborted due to too many errors."])}</p>
            : null}
            {uploadResults.warnings.length > 0 ?
              <>
                <p>
                  {t(
                    ($) =>
                      $.merchandising[
                        "Some rows matched a product whose identifier is written differently in Trellis. Update your file to keep them in step."
                      ],
                  )}
                </p>
                <ResultTable
                  secondColumnLabel={t(($) => $.merchandising["Matched"])}
                  rows={uploadResults.warnings.map((warning, index) => ({
                    key: `${warning.mapped?.sku}-${index}`,
                    sku: warning.mapped?.sku,
                    message: t(($) => $.merchandising["Matched {sku} / {asin} (case-insensitive)"], {
                      sku: warning.matched_by?.sku ?? "",
                      asin: warning.matched_by?.asin ?? "",
                    }),
                  }))}
                />
              </>
            : null}
            {uploadResults.errors.length >= 500 ?
              <p>
                {t(($) => $.merchandising["Cancelled import after more than 500 errors occurred. Please see below."])}
              </p>
            : null}
            <ResultTable
              secondColumnLabel={t(($) => $.merchandising["Error"])}
              emptyMessage={t(($) => $.merchandising["No errors occurred."])}
              rows={uploadResults.errors.map(({ error }, index) => ({
                key: `${error.mapped?.sku}-${index}`,
                sku: error.mapped?.sku,
                message: error.error?.msg,
              }))}
            />
          </div>
          <div className={css({ display: "flex", justifyContent: "flex-end", gap: "small", paddingX: "large" })}>
            <Button variant="secondary" onPress={props.onClose}>
              {t(($) => $.common.actions.Close)}
            </Button>
          </div>
        </>
      : null}
    </Modal>
  );
};
